import { Box, Container } from "@mui/material";
import {
  LIST_USERS,
  LIST_ADMINS,
  LIST_SUPER_ADMINS,
  DELETE_USER,
  LIST_GRADES_AND_CLASSROOMS_BY_PERIOD,
  LIST_GROUPS,
  LIST_USER_UIDS_BY_GROUPS,
} from "../../../graphql/admin-requests";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import CUModal from "../modals/CUModal";
import CUUser from "../CreateUpdateComponents/User";
import CUAdmin from "../CreateUpdateComponents/Admin";
import CUSuperAdmin from "../CreateUpdateComponents/SuperAdmin";
import React, { useState, useEffect, useCallback } from "react";
import UserGroupModal from "../modals/UserGroupModal";
import UserGroups from "../UserGroups";
import AdminClassrooms from "../AdminClassrooms";
import UserListResults from "./UserListResults";
import AdminListResults from "./AdminListResults";
import SuperAdminListResults from "./SuperAdminListResults";
import UserListToolbar from "./UserListToolbar";
import AdminListToolbar from "./AdminListToolbar";
import SuperAdminListToolbar from "./SuperAdminListToolbar";
import UserDialog from "./UserDialog";
import CSVUserImporter from "../CSVUserImporter";

function UserList({ role = "users", period }) {
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(5);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");

  const [classroomOptions, setClassroomOptions] = useState([]);
  const [classroomsState, setClassroomsState] = useState([]);
  const [groupsState, setGroupsState] = useState([]);

  const [openCUUserModal, setOpenCUUserModal] = useState(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const [openUserGroupsModal, setOpenUserGroupsModal] = useState(false);
  const [openAdminClassroomsModal, setOpenAdminClassroomsModal] =
    useState(false);
  const [openCSVImportModal, setOpenCSVImportModal] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState({});
  const [userToDelete, setUserToDelete] = useState({});

  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: (data) => console.log("Data from delete", data),
    onError: (error) => console.error("Error deleting an activity", error),
  });

  const handleOpenCUUserModal = () => {
    setOpenCUUserModal(true);
  };

  const handleOpenCSVImportModal = () => {
    setOpenCSVImportModal(true);
  };

  const handleCloseCSVImportModal = () => {
    setOpenCSVImportModal(false);
    loadData(); // Reload data after import
  };

  const handleCSVImportComplete = (data) => {
    console.log('CSV import completed:', data);
    // You can add additional logic here if needed
  };

  const handleDeleteUser = (user) => async () => {
    setUserToDelete(user);
    setOpenDeleteUserModal(true);
  };

  const handleDeleteUserConfirm = async () => {
    await deleteUser({ variables: { userUid: userToDelete.uid } });
    setOpenDeleteUserModal(false);
    loadData();
  };

  const handleUpdateUserGroups = (user) => () => {
    setUserToUpdate(user);
    setOpenUserGroupsModal(true);
  };

  const handleUpdateAdminClassrooms = (admin) => () => {
    setUserToUpdate(admin);
    setOpenAdminClassroomsModal(true);
  };

  const handleOpen = () => {
    setOpenCUUserModal(true);
  };

  const handleClose = () => {
    setOpenCUUserModal(false);
    setOpenUserGroupsModal(false);
    setOpenAdminClassroomsModal(false);
    loadData();
  };

  const queryToUse = {
    users: LIST_USERS,
    admins: LIST_ADMINS,
    superadmins: LIST_SUPER_ADMINS,
  }[role];

  const [getUsers] = useLazyQuery(queryToUse, {
    onCompleted: (data) => {
      setUsers(data.allUsers.nodes);
      setTotalCount(data.allUsers.totalCount);
    },
    fetchPolicy: "cache-and-network",
  });

  // Niveau/classe and groupe filter options (only needed for the student list)
  useQuery(LIST_GRADES_AND_CLASSROOMS_BY_PERIOD, {
    variables: { periodUid: period && period.uid },
    skip: role !== "users" || !period || !period.uid,
    onCompleted: (data) => {
      const options = data.allPeriodGrades.nodes.map((i) => {
        const grade = i.gradeByGradeUid;
        return {
          uid: grade.uid,
          name: grade.name,
          choices: grade.classroomsByGradeUid.nodes.map((c) => ({ uid: c.uid, name: c.name, gradeUid: grade.uid })),
        };
      });
      const cState = data.allPeriodGrades.nodes
        .map(({ gradeByGradeUid: { classroomsByGradeUid: { nodes }, uid } }) =>
          nodes.map((n) => ({ ...n, checked: false, gradeUid: uid })))
        .flat();
      setClassroomOptions(options);
      setClassroomsState(cState);
    },
    fetchPolicy: "cache-and-network",
  });

  useQuery(LIST_GROUPS, {
    skip: role !== "users",
    onCompleted: (data) => {
      setGroupsState(data.allGroups.nodes.map((g) => ({ ...g, checked: false })));
    },
    fetchPolicy: "cache-and-network",
  });

  const [getUserUidsByGroups] = useLazyQuery(LIST_USER_UIDS_BY_GROUPS);

  const updateSearch = (event) => {
    setSearch(event.target.value);
  };

  const loadData = useCallback(async () => {
    const searchF = {
      or: [
        { lastName: { includesInsensitive: search } },
        { firstName: { includesInsensitive: search } },
      ],
    };

    const conditions = [searchF];

    const selectedClassroomUids = classroomsState.filter((c) => c.checked).map((c) => c.uid);
    if (selectedClassroomUids.length > 0) {
      conditions.push({ classroomUid: { in: selectedClassroomUids } });
    }

    const selectedGroupUids = groupsState.filter((g) => g.checked).map((g) => g.uid);
    if (selectedGroupUids.length > 0) {
      const { data } = await getUserUidsByGroups({ variables: { groupUids: selectedGroupUids } });
      const matchingUserUids = [...new Set((data?.allUserGroups?.nodes || []).map((n) => n.userUid))];
      // An empty `in` still has to mean "no match" - fall back to a UUID that can't exist.
      conditions.push({ uid: { in: matchingUserUids.length > 0 ? matchingUserUids : ["00000000-0000-0000-0000-000000000000"] } });
    }

    getUsers({
      variables: {
        filter: { and: conditions },
        first: limit,
        offset,
      },
    });
  }, [getUsers, getUserUidsByGroups, limit, offset, search, classroomsState, groupsState]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Container component="main" maxWidth={false}>
      <UserDialog
        user={userToDelete}
        onConfirm={handleDeleteUserConfirm}
        onCancel={() => {
          setOpenDeleteUserModal(false);
        }}
        open={openDeleteUserModal}
      />
      {role === "users" && (
        <CUModal
          role={role}
          open={openCUUserModal}
          handleOpen={handleOpen}
          handleClose={handleClose}
          component={CUUser}
        />
      )}
      {role === "admins" && (
        <CUModal
          role={role}
          open={openCUUserModal}
          handleOpen={handleOpen}
          handleClose={handleClose}
          component={CUAdmin}
        />
      )}
      {role === "superadmins" && (
        <CUModal
          role={role}
          open={openCUUserModal}
          handleOpen={handleOpen}
          handleClose={handleClose}
          component={CUSuperAdmin}
        />
      )}
      <UserGroupModal
        open={openUserGroupsModal}
        handleOpen={handleOpen}
        onFinish={handleClose}
        user={userToUpdate}
        component={UserGroups}
      />
      <UserGroupModal
        open={openAdminClassroomsModal}
        handleOpen={handleOpen}
        onFinish={handleClose}
        user={userToUpdate}
        component={AdminClassrooms}
      />
      <Box
        sx={{ backgroundColor: "background.default", minHeight: "100%", py: 3 }}
      >
        <Container maxWidth={false}>
          {role === "users" && (
            <UserListToolbar
              handleOpenCUUserModal={handleOpenCUUserModal}
              handleOpenCSVImportModal={handleOpenCSVImportModal}
              updateSearch={updateSearch}
              classroomOptions={classroomOptions}
              classroomsState={classroomsState}
              setClassroomsState={setClassroomsState}
              groupsState={groupsState}
              setGroupsState={setGroupsState}
            />
          )}

          {role === "admins" && (
            <AdminListToolbar
              handleOpenCUUserModal={handleOpenCUUserModal}
              updateSearch={updateSearch}
            />
          )}

          {role === "superadmins" && (
            <SuperAdminListToolbar
              handleOpenCUUserModal={handleOpenCUUserModal}
              updateSearch={updateSearch}
            />
          )}

          <Box sx={{ pt: 3 }}>
            {role === "users" && (
              <UserListResults
                period={period}
                handleUpdateUserGroups={handleUpdateUserGroups}
                handleDeleteUser={handleDeleteUser}
                users={users}
                loadData={loadData}
                totalCount={totalCount}
                setLimit={setLimit}
                limit={limit}
                setOffset={setOffset}
                offset={offset}
              />
            )}
            {role === "admins" && (
              <AdminListResults
                period={period}
                handleUpdateAdminClassrooms={handleUpdateAdminClassrooms}
                handleDeleteUser={handleDeleteUser}
                users={users}
                loadData={loadData}
                totalCount={totalCount}
                setLimit={setLimit}
                limit={limit}
                setOffset={setOffset}
                offset={offset}
              />
            )}
            {role === "superadmins" && (
              <SuperAdminListResults
                period={period}
                handleDeleteUser={handleDeleteUser}
                users={users}
                loadData={loadData}
                totalCount={totalCount}
                setLimit={setLimit}
                limit={limit}
                setOffset={setOffset}
                offset={offset}
              />
            )}
          </Box>
        </Container>
      </Box>
      {role === "users" && (
        <CSVUserImporter
          isOpen={openCSVImportModal}
          onClose={handleCloseCSVImportModal}
          onComplete={handleCSVImportComplete}
        />
      )}
    </Container>
  );
}

export default UserList;
